import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import './index.css';
import {
  registerServiceWorker,
  setupInstallPrompt,
  unregisterServiceWorker,
} from './lib/service-worker';
import { resolveApiBaseUrl, getApiBase } from './api/fetch';
import { relayGoogleConnectIfPopup } from './api/googleConnectBridge';

if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

// Debug: log env/config in use (strip out later)
const webConfigForDebug = () => {
  const base = resolveApiBaseUrl();
  console.log('[Web Config]', {
    VITE_API_URL: import.meta.env.VITE_API_URL ?? '(empty)',
    resolveApiBaseUrl: base || '(empty → using proxy/same origin)',
    getApiBase: getApiBase(),
    origin: typeof window !== 'undefined' ? window.location.origin : '(ssr)',
    hasApiKey: typeof localStorage !== 'undefined' ? !!localStorage.getItem('danny_api_key') : false,
    danny_api_url_override: typeof localStorage !== 'undefined' ? localStorage.getItem('danny_api_url_override') : null,
  });
};
if (typeof window !== 'undefined') webConfigForDebug();

// In local/ngrok dev, an old service worker can keep serving stale bundles to the
// installed PWA. Unregister it proactively so iPhone testing uses the current code.
if (import.meta.env.DEV) {
  unregisterServiceWorker().catch(() => {});
}

// Register service worker for PWA functionality
if (import.meta.env.PROD) {
  registerServiceWorker();
  setupInstallPrompt();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30s
      retry: 1,
    },
  },
});

// Extension side panel: the Google Calendar OAuth flow runs in a top-level
// popup (Google can't be framed). When that popup lands back on
// /app?google_connect=..., relay the result to the opener iframe and close —
// don't render the whole app inside the throwaway popup.
if (!relayGoogleConnectIfPopup()) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}

